import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { getToken, removeToken, setToken, login, getCurrentUser, AuthenticationError, AuthorizationError, type LoginCredentials, type CurrentUser } from "@/lib/api";

interface UserData {
  id: number;
  email: string;
  role: string;
}

interface AuthContextType {
  user: UserData | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (credentials: LoginCredentials) => Promise<void>;
  logout: () => void;
  refetchUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Convert CurrentUser from API to UserData
function convertUserData(apiUser: CurrentUser): UserData {
  return {
    id: apiUser.id,
    email: apiUser.email,
    role: apiUser.role,
  };
}

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<UserData | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch user from backend to verify token
  const fetchUser = async () => {
    const token = getToken();
    if (!token) {
      setIsLoading(false);
      return;
    }

    try {
      const currentUser = await getCurrentUser();
      const userData = convertUserData(currentUser);
      
      // Check if user is admin, if not logout
      if (userData.role !== "admin") {
        console.warn("User is not admin, logging out. Role:", userData.role);
        removeToken();
        setUser(null);
        return;
      }
      
      setUser(userData);
    } catch (error) {
      // If authentication fails (401) or authorization fails (403), logout
      if (error instanceof AuthenticationError || error instanceof AuthorizationError) {
        console.error("Auth error - logging out:", error.message);
        removeToken();
        setUser(null);
      } else {
        // For other errors, just clear user but keep token (might be network issue)
        console.error("Failed to fetch user:", error);
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchUser();
  }, []);

  const handleLogin = async (credentials: LoginCredentials) => {
    const response = await login(credentials);
    setToken(response.access_token);
    // Fetch user data from backend after login
    try {
      const currentUser = await getCurrentUser();
      const userData = convertUserData(currentUser);
      // Check if user is admin, if not logout immediately
      if (userData.role !== "admin") {
        removeToken();
        throw new AuthorizationError("Admin access required");
      }
      setUser(userData);
    } catch (error) {
      console.error("Failed to fetch user after login:", error);
      // If fetch fails or user is not admin, logout
      removeToken();
      throw error;
    }
  };

  const handleLogout = () => {
    removeToken();
    setUser(null);
  };

  const handleRefetchUser = async () => {
    await fetchUser();
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login: handleLogin,
    logout: handleLogout,
    refetchUser: handleRefetchUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

